/**
 * Live alerts (T11-52): a short message (toast) and a quiet "ding" when something the staff must
 * notice happens (new order, a customer says they paid, money received, a counter sale, the same
 * card sold twice, a new consignment request). Per person, in this browser (localStorage): both on
 * by default; switched in the "● Live" menu of the header or the sound button of the home page.
 */
import { useSyncExternalStore } from "react";

export interface LiveAlertPrefs {
  /** Show a toast for the changes of `ALERT_RULES`. */
  toasts: boolean;
  /** Play the quiet sound with them. */
  sound: boolean;
}

const DEFAULTS: LiveAlertPrefs = { toasts: true, sound: true };
/** The home page's sound switch of T11-10 ("off" = the person switched it off before). */
const LEGACY_SOUND_KEY = "tcg.admin.dashboard.sound";

export const liveAlertsKey = (staffId: string) => `tcg.admin.live-alerts.${staffId}`;

const cache = new Map<string, LiveAlertPrefs>();
const listeners = new Set<() => void>();

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function loadLiveAlertPrefs(staffId: string): LiveAlertPrefs {
  const hit = cache.get(staffId);
  if (hit) {
    return hit;
  }
  let prefs = DEFAULTS;
  try {
    const store = storage();
    const raw = store?.getItem(liveAlertsKey(staffId));
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<LiveAlertPrefs>;
      prefs = { toasts: parsed.toasts !== false, sound: parsed.sound !== false };
    } else if (store?.getItem(LEGACY_SOUND_KEY) === "off") {
      prefs = { ...DEFAULTS, sound: false };
    }
  } catch {
    prefs = DEFAULTS;
  }
  cache.set(staffId, prefs);
  return prefs;
}

export function saveLiveAlertPrefs(staffId: string, prefs: LiveAlertPrefs): void {
  cache.set(staffId, prefs);
  try {
    storage()?.setItem(liveAlertsKey(staffId), JSON.stringify(prefs));
  } catch {
    // private mode: the choice lasts for this tab
  }
  for (const listener of listeners) {
    listener();
  }
}

/** Tests: forget what was read from storage. */
export function resetLiveAlertPrefsCache(): void {
  cache.clear();
}

export function useLiveAlertPrefs(staffId: string): LiveAlertPrefs {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => loadLiveAlertPrefs(staffId),
    () => DEFAULTS,
  );
}

/** A short, quiet "ding" (WebAudio — no sound file to download). */
export function playDing(): void {
  try {
    const AudioCtx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) {
      return;
    }
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
    osc.onended = () => {
      void ctx.close();
    };
  } catch {
    // no sound possible (autoplay rules) — the toast is the main signal anyway
  }
}
